"use client";

import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../../../components/navbar";
import Footer from "../../../components/footer";
import Pagination from "../../../components/Pagination";
import { Search } from "lucide-react";
import UnpaidOrderOptionsModal from "./UnpaidOrderOptionsModal";
import styles from "./css/codUnpaidOrders.module.css";

const MOCK_ORDERS = [
  { id: "E89655231", customer: "Pamela Smith", status: "Unpaid", items: 5, amount: 85.0 },
  { id: "E10245358", customer: "Sofia Reynolds", status: "Unpaid", items: 2, amount: 85.0 },
  { id: "E45698712", customer: "Carlos Sainz", status: "Unpaid", items: 4, amount: 85.0 },
];

export default function CodUnpaidOrders() {
  const location = useLocation();
  const navigate = useNavigate();
  const [search, setSearch] = React.useState("");
  const [currentPage, setCurrentPage] = React.useState(1);
  const pageSize = 5;
  const [selectedOrderState, setSelectedOrderState] = React.useState(null);
  const [showOptionsModal, setShowOptionsModal] = React.useState(false);

  const fromSplit = !!(location.state && location.state.splitFlow);
  const fromDebtors = !!(location.state && location.state.fromDebtors);
  const splitTotal = location.state && typeof location.state.total === "number" ? location.state.total : null;

  const handleBack = () => {
    if (fromSplit) {
      navigate("/frontshop/payment-types/split", { state: { total: splitTotal || 0, fromDebtors } });
      return;
    }
    navigate("/frontshop/payment-types");
  };

  const filteredOrders = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return MOCK_ORDERS;
    return MOCK_ORDERS.filter((order) => `${order.customer} ${order.id}`.toLowerCase().includes(q));
  }, [search]);

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / pageSize));

  const pageData = React.useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredOrders.slice(start, start + pageSize);
  }, [filteredOrders, currentPage]);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  React.useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  const handleRowClick = (order) => {
    const baseState = {
      orderId: order.id,
      recieptno: order.id,
      customer: order.customer,
      total: fromSplit && splitTotal != null ? splitTotal : order.amount,
    };
    const optionsState = fromSplit
      ? {
          ...baseState,
          splitFlow: true,
          method1: location.state && typeof location.state.method1 === "string" ? location.state.method1 : "cod",
          fromDebtors,
        }
      : baseState;
    setSelectedOrderState(optionsState);
    setShowOptionsModal(true);
    navigate("/frontshop/payment-types/cod/options", { state: optionsState });
  };

  const handleCloseModal = () => {
    setShowOptionsModal(false);
    navigate("/frontshop/payment-types/cod", { state: location.state });
  };

  const handleContinueToPayment = () => {
    if (!selectedOrderState) return;
    navigate("/frontshop/payment-types/cod/payment", { state: selectedOrderState });
  };

  const handleRefund = () => {
    if (!selectedOrderState) {
      setShowOptionsModal(false);
      navigate("/frontshop/payment-types/cod", { state: location.state });
      return;
    }
    setShowOptionsModal(false);
    navigate("/frontshop/payment-types/cod/refund-items", { state: selectedOrderState });
  };

  React.useEffect(() => {
    if (
      location.pathname === "/frontshop/payment-types/cod/options" &&
      location.state &&
      !showOptionsModal
    ) {
      setSelectedOrderState(location.state);
      setShowOptionsModal(true);
    }
  }, [location, showOptionsModal]);

  return (
    <div className={styles.wrapper}>
      <Navbar onBack={handleBack} />
      <main className={styles.main}>
        <div className={styles.card}>
          <div className={styles.headerRow}>
            <h1 className={styles.title}>Unpaid Orders</h1>
            <div className={styles.searchBox}>
              <Search size={16} className={styles.searchIcon} />
              <input
                className={styles.searchInput}
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className={styles.list}>
            {pageData.map((order) => (
              <button
                key={order.id}
                type="button"
                className={styles.row}
                onClick={() => handleRowClick(order)}
              >
                <div className={styles.rowMain}>
                  <div className={styles.name}>
                    {order.customer} - {order.id}
                  </div>
                  <div className={styles.metaRow}>
                    <span className={styles.status}>Unpaid</span>
                    <span className={styles.itemsBadge}>{order.items} Items</span>
                  </div>
                </div>
                <div className={styles.amount}>R {order.amount.toFixed(2)}</div>
              </button>
            ))}
          </div>
          {filteredOrders.length > pageSize && (
            <div className={styles.paginationWrap}>
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            </div>
          )}
        </div>
      </main>
      <div className={styles.footerWrapper}>
        <Footer />
      </div>
      <UnpaidOrderOptionsModal
        isOpen={showOptionsModal}
        onRefund={handleRefund}
        onContinue={handleContinueToPayment}
        onClose={handleCloseModal}
      />
    </div>
  );
}
